// src/pages/OniEvolvePage.js
// Evolve = progression visible du démon via les perks et le journal
// Différent de Reform (recalibration complète) — ici on ajoute des perks
// débloqués par l'activité (journal, duels, coach)

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { PERKS_LIST, computeLevel, computeDemon } from "../data/demons";
import DemonCard from "../components/DemonCard";
import EmptyState from "../components/EmptyState";
import "./OniEvolvePage.css";

// Perks débloquables selon l'activité
const UNLOCK_CONDITIONS = [
  { perkId:"rager",        label:"Rager",               condition: p => p.journal?.filter(e=>e.verdict==="DÉFAITE").length >= 3,  desc:"3 défaites inscrites dans le journal" },
  { perkId:"screamer",     label:"Screamer",             condition: p => p.weeklyDuelsWon >= 3,                                    desc:"3 duels gagnés cette semaine" },
  { perkId:"old_monkey",   label:"Old Monkey Tactics",  condition: p => (p.xp||0) >= 500,                                         desc:"500 XP accumulés" },
  { perkId:"trick_shot",   label:"Trick Shot",           condition: p => p.journal?.length >= 10,                                  desc:"10 chroniques de journal" },
  { perkId:"equipment_junky", label:"Equipment Junky",  condition: p => (p.xp||0) >= 250,                                         desc:"250 XP accumulés" },
  { perkId:"fisher",       label:"Fisher",               condition: p => p.journal?.filter(e=>e.verdict==="VICTOIRE").length >= 5, desc:"5 victoires dans le journal" },
  { perkId:"discipline",   label:"Discipline",           condition: p => p.journal?.length >= 20,                                  desc:"20 chroniques de journal" },
  { perkId:"defender",     label:"Defender",             condition: p => (p.xp||0) >= 900,                                         desc:"900 XP — Niveau 5" },
  { perkId:"looper",       label:"Looper",               condition: p => p.journal?.filter(e=>e.verdict==="PROGRESSION").length >= 5, desc:"5 sessions PROGRESSION" },
  { perkId:"high_toss",    label:"High Toss",            condition: p => p.journal?.filter(e=>e.verdict==="CHAOS").length >= 3,    desc:"3 sessions CHAOS" },
];

export default function OniEvolvePage() {
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [selected, setSelected] = useState([]);

  const demon    = profile?.demon;
  const xp       = profile?.xp || 0;
  const level    = computeLevel(xp);
  const journal  = profile?.journal || [];
  const currentPerks = demon?.perks || [];

  // Calcule quels perks sont débloqués et pas encore actifs
  const unlockable = UNLOCK_CONDITIONS.map(u => ({
    ...u,
    unlocked:   u.condition(profile || {}),
    alreadyHas: currentPerks.includes(u.perkId),
  }));

  const available  = unlockable.filter(u => u.unlocked && !u.alreadyHas);
  const locked     = unlockable.filter(u => !u.unlocked && !u.alreadyHas);
  const active     = unlockable.filter(u => u.alreadyHas);

  const toggleSelect = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const applyPerks = async () => {
    if (!user || !selected.length) return;
    setSaving(true);
    try {
      const newPerks  = [...new Set([...currentPerks, ...selected])];
      const newDemon  = computeDemon(
        { style: demon?.quizAnswers?.style || "attacker",
          mental: demon?.quizAnswers?.mental || "clutch",
          matos:  demon?.quizAnswers?.matos  || "stable",
          behavior: demon?.quizAnswers?.behavior || "silent" },
        newPerks
      );
      await updateDoc(doc(db, "users", user.uid), {
        demon: { ...newDemon, savedAt: serverTimestamp() },
        xp: xp + selected.length * 20,
      });
      await refreshProfile();
      setSelected([]);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  if (!user) return (
    <div className="evolve-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <Link to="/auth" className="btn btn-primary">ENTRER →</Link>
    </div>
  );

  if (!demon) return (
    <div className="evolve-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <h2 className="section-title">Pas encore de démon à faire évoluer</h2>
      <Link to="/awaken" className="btn btn-primary" style={{marginTop:"1.5rem"}}>RÉVEILLER →</Link>
    </div>
  );

  return (
    <div className="evolve-page">
      <div className="section" style={{paddingTop:"7rem"}}>

        <div className="evolve-header">
          <div>
            <span className="section-eyebrow">進化 · Oni Evolve</span>
            <h1 className="section-title">Faire évoluer<br/>ton Démon</h1>
            <div className="divider"/>
            <p className="section-sub">
              Différent du Reform — pas de recalibration complète.
              Tu débloque des perks par ton activité et les appliques à ton démon actuel.
            </p>
          </div>
          <div className="evolve-demon-badge">
            <span>{demon.oni?.emoji}</span>
            <div>
              <p className="evolve-oni-jp">{demon.oni?.jp}</p>
              <p className="evolve-oni-name">{demon.oni?.name}</p>
              <p className="evolve-level">{level.current.badge} LV.{level.current.level} · {xp} XP</p>
            </div>
          </div>
        </div>

        <div className="evolve-grid">

          {/* Left — Perks disponibles */}
          <div className="evolve-main">

            {/* Perks débloqués et sélectionnables */}
            {available.length > 0 && (
              <div className="evolve-section">
                <p className="evolve-section-title">
                  ✦ PERKS DÉBLOQUÉS · {available.length} DISPONIBLE{available.length>1?"S":""}
                </p>
                <p className="evolve-section-sub">Sélectionne ceux à appliquer à ton démon.</p>
                <div className="evolve-perks-grid">
                  {available.map(u => (
                    <button
                      key={u.perkId}
                      className={`evolve-perk-card${selected.includes(u.perkId) ? " selected" : ""}`}
                      onClick={() => toggleSelect(u.perkId)}
                    >
                      <div className="evolve-perk-header">
                        <span className="evolve-perk-name">{u.label}</span>
                        <span className="evolve-perk-check">{selected.includes(u.perkId) ? "✓" : "+"}</span>
                      </div>
                      <p className="evolve-perk-cond">🔓 {u.desc}</p>
                    </button>
                  ))}
                </div>

                {selected.length > 0 && (
                  <div className="evolve-apply-bar">
                    <span>{selected.length} perk{selected.length>1?"s":""} sélectionné{selected.length>1?"s":""} · +{selected.length*20} XP</span>
                    <button className="btn btn-primary btn-sm" onClick={applyPerks} disabled={saving}>
                      {saving ? <><span className="spinner"/> APPLICATION...</>
                        : saved ? "✓ APPLIQUÉ"
                        : "APPLIQUER À MON DÉMON →"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Perks verrouillés — objectifs à atteindre */}
            <div className="evolve-section">
              <p className="evolve-section-title">🔒 PROCHAINS DÉBLOCAGES</p>
              <div className="evolve-locked-list">
                {locked.map(u => (
                  <div key={u.perkId} className="evolve-locked-item">
                    <div className="evolve-locked-left">
                      <span className="evolve-locked-name">{u.label}</span>
                      <span className="evolve-locked-cond">{u.desc}</span>
                    </div>
                    <span className="evolve-locked-icon">🔒</span>
                  </div>
                ))}
                {locked.length === 0 && (
                  <EmptyState type="evolve_all_done"/>
                )}
              </div>
            </div>

            {/* Perks déjà actifs */}
            {active.length > 0 && (
              <div className="evolve-section">
                <p className="evolve-section-title">✓ DÉJÀ ACTIFS</p>
                <div className="evolve-active-list">
                  {active.map(u => (
                    <span key={u.perkId} className="evolve-active-tag">✓ {u.label}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right — Aperçu du démon */}
          <div className="evolve-side">
            <p className="evolve-section-title" style={{marginBottom:"1rem"}}>APERÇU DÉMON ACTUEL</p>
            <DemonCard demon={demon} xp={xp} compact />

            <div className="evolve-actions">
              <Link to="/reform" className="btn btn-ghost btn-sm" style={{justifyContent:"center"}}>
                🔥 ONI REFORM (recalibration complète)
              </Link>
              <Link to="/journal" className="btn btn-ghost btn-sm" style={{justifyContent:"center"}}>
                📜 JOURNAL → débloquer des perks
              </Link>
              <Link to="/duel" className="btn btn-ghost btn-sm" style={{justifyContent:"center"}}>
                ⚔️ DUEL → gagner des duels
              </Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
